import React, { useState } from 'react';
import {
  Sparkles,
  Sliders,
  Palette,
  Eye,
  Layers,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  RotateCcw,
  Check,
  CheckCircle2,
  Copy,
  Info,
  Maximize2
} from 'lucide-react';
import { 
  ElementMaterialFinish, 
  ElementMaterialBumpMap, 
  ElementMaterialConfig, 
  VdpElement 
} from '../../types';
import { 
  MATERIAL_FINISH_PRESETS, 
  QUICK_FOIL_SWATCHES 
} from '../../utils/elementMaterialRenderer';

export const BUMP_MAP_PRESETS: { id: ElementMaterialBumpMap; name: string; icon: string; desc: string }[] = [
  { id: 'none', name: 'Smooth (No Bump)', icon: '⚪', desc: 'Completely uniform flat surface' },
  { id: 'smooth_bevel', name: 'Sculpted Bevel Edge', icon: '🏔️', desc: 'Soft beveled contour ideal for embossing' },
  { id: 'hammered_foil', name: 'Hammered Luxury Foil', icon: '✨', desc: 'Micro-faceted artisan metal indentations' },
  { id: 'brushed_metal', name: 'Brushed Anisotropic Metal', icon: '🪙', desc: 'Directional micro-grain linear brushing' },
  { id: 'fine_leather', name: 'Fine Grain Leather', icon: '🐂', desc: 'Subtle organic pebble texture' },
  { id: 'linen_canvas', name: 'Woven Linen Canvas', icon: '🧵', desc: 'Cross-hatch textured fabric matrix' },
  { id: 'micro_diamond', name: 'Micro Diamond Knurl', icon: '💎', desc: 'Precision geometric tactile diamond grid' },
  { id: 'carbon_fiber', name: 'Twill Carbon Fiber', icon: '🏁', desc: 'Technical diagonal composite weave' },
  { id: 'noise_grain', name: 'Stippled Sandblast Grain', icon: '🌫️', desc: 'Heavy particulate diffuse texture' }
];

interface MaterialDetailSettingsPanelProps {
  selectedElements: VdpElement[];
  onUpdateElements: (updates: Partial<VdpElement>) => void;
  title?: string;
  isCompact?: boolean;
}

export const MaterialDetailSettingsPanel: React.FC<MaterialDetailSettingsPanelProps> = ({
  selectedElements,
  onUpdateElements,
  title = 'Material & Tactile Finishes',
  isCompact = false
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [showBumpDetails, setShowBumpDetails] = useState<boolean>(true);
  const [copiedSuccess, setCopiedSuccess] = useState<boolean>(false);

  if (selectedElements.length === 0) return null;

  const firstEl = selectedElements[0];
  const isMulti = selectedElements.length > 1;

  // Determine current effective properties
  const currentFinish: ElementMaterialFinish = (firstEl.materialFinish || firstEl.materialConfig?.finish || 'default');
  const activePreset = MATERIAL_FINISH_PRESETS.find(p => p.id === currentFinish) || MATERIAL_FINISH_PRESETS[0];

  const currentMetalness = firstEl.materialMetalness !== undefined 
    ? firstEl.materialMetalness 
    : (firstEl.materialConfig?.metalness !== undefined ? firstEl.materialConfig.metalness : activePreset.defaultMetalness);

  const currentRoughness = firstEl.materialRoughness !== undefined 
    ? firstEl.materialRoughness 
    : (firstEl.materialConfig?.roughness !== undefined ? firstEl.materialConfig.roughness : activePreset.defaultRoughness);

  const currentRelief = firstEl.materialRelief !== undefined 
    ? firstEl.materialRelief 
    : (firstEl.materialConfig?.reliefHeight !== undefined ? firstEl.materialConfig.reliefHeight : activePreset.defaultRelief);

  const currentColor = firstEl.materialColor || firstEl.materialConfig?.color || activePreset.defaultColor;

  const currentBumpMap: ElementMaterialBumpMap = firstEl.materialBumpMapType || firstEl.materialConfig?.bumpMapType || (
    currentFinish === 'embossed' || currentFinish === 'debossed' ? 'smooth_bevel' : 'none'
  );

  const currentBumpScale = firstEl.materialBumpScale !== undefined 
    ? firstEl.materialBumpScale 
    : (firstEl.materialConfig?.bumpScale !== undefined ? firstEl.materialConfig.bumpScale : 1.0);

  const currentBumpRepeat = firstEl.materialBumpRepeat !== undefined 
    ? firstEl.materialBumpRepeat 
    : (firstEl.materialConfig?.bumpRepeat !== undefined ? firstEl.materialConfig.bumpRepeat : 6);

  const currentInvertBump = firstEl.materialInvertBump !== undefined 
    ? firstEl.materialInvertBump 
    : (firstEl.materialConfig?.invertBump || currentFinish === 'debossed');

  const currentBevelSoftness = firstEl.materialBevelSoftness !== undefined 
    ? firstEl.materialBevelSoftness 
    : (firstEl.materialConfig?.bevelSoftness !== undefined ? firstEl.materialConfig.bevelSoftness : 0.35);

  const applyMaterialUpdates = (updates: {
    finish?: ElementMaterialFinish;
    metalness?: number;
    roughness?: number;
    relief?: number;
    color?: string;
    bumpMapType?: ElementMaterialBumpMap;
    bumpScale?: number;
    bumpRepeat?: number;
    invertBump?: boolean;
    bevelSoftness?: number;
  }) => {
    const updatedConfig: ElementMaterialConfig = {
      finish: updates.finish ?? currentFinish,
      metalness: updates.metalness ?? currentMetalness,
      roughness: updates.roughness ?? currentRoughness,
      reliefHeight: updates.relief ?? currentRelief,
      color: updates.color ?? currentColor,
      bumpMapType: updates.bumpMapType ?? currentBumpMap,
      bumpScale: updates.bumpScale ?? currentBumpScale,
      bumpRepeat: updates.bumpRepeat ?? currentBumpRepeat,
      invertBump: updates.invertBump ?? currentInvertBump,
      bevelSoftness: updates.bevelSoftness ?? currentBevelSoftness
    };

    onUpdateElements({
      materialFinish: updatedConfig.finish,
      materialMetalness: updatedConfig.metalness,
      materialRoughness: updatedConfig.roughness,
      materialRelief: updatedConfig.reliefHeight,
      materialColor: updatedConfig.color,
      materialBumpMapType: updatedConfig.bumpMapType,
      materialBumpScale: updatedConfig.bumpScale,
      materialBumpRepeat: updatedConfig.bumpRepeat,
      materialInvertBump: updatedConfig.invertBump,
      materialBevelSoftness: updatedConfig.bevelSoftness,
      materialConfig: updatedConfig
    });
  };

  const handleSelectPreset = (presetId: ElementMaterialFinish) => {
    const preset = MATERIAL_FINISH_PRESETS.find(p => p.id === presetId) || MATERIAL_FINISH_PRESETS[0];
    const defaultBump: ElementMaterialBumpMap = 
      presetId === 'embossed' ? 'smooth_bevel' :
      presetId === 'debossed' ? 'smooth_bevel' :
      presetId === 'gold_foil' ? 'hammered_foil' : 'none';

    applyMaterialUpdates({
      finish: presetId,
      metalness: preset.defaultMetalness,
      roughness: preset.defaultRoughness,
      relief: preset.defaultRelief,
      color: preset.defaultColor,
      bumpMapType: defaultBump,
      invertBump: presetId === 'debossed'
    });
  };

  const handleReset = () => {
    onUpdateElements({
      materialFinish: 'default',
      materialMetalness: undefined,
      materialRoughness: undefined,
      materialRelief: undefined,
      materialColor: undefined,
      materialBumpMapType: 'none',
      materialBumpScale: 1.0,
      materialBumpRepeat: 6,
      materialInvertBump: false,
      materialBevelSoftness: 0.35,
      materialConfig: undefined
    });
  };

  return (
    <div className="bg-zinc-900/90 border border-zinc-800 rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div 
        onClick={() => setIsExpanded(!isExpanded)}
        className="px-3 py-2.5 bg-zinc-950/80 border-b border-zinc-800/80 flex items-center justify-between cursor-pointer select-none hover:bg-zinc-900/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-md bg-gradient-to-tr from-amber-500 to-yellow-300 flex items-center justify-center text-zinc-950 shadow-xs">
            <Sparkles className="w-3 h-3 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-xs font-bold text-zinc-200">{title}</span>
            {isMulti && (
              <span className="ml-1.5 text-[10px] px-1.5 py-0.2 rounded-full bg-blue-500/20 text-blue-400 font-mono">
                {selectedElements.length} items
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-amber-400 font-semibold px-1.5 py-0.5 rounded bg-zinc-900 border border-zinc-800">
            {activePreset.name}
          </span>
          {isExpanded ? <ChevronUp className="w-3.5 h-3.5 text-zinc-400" /> : <ChevronDown className="w-3.5 h-3.5 text-zinc-400" />}
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 space-y-3 text-xs">
          
          {/* Quick Preset Strip */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10.5px] font-semibold text-zinc-400">Finish Preset:</label>
              <button
                type="button"
                onClick={handleReset}
                className="text-[9.5px] text-zinc-500 hover:text-rose-400 flex items-center gap-1 transition-colors cursor-pointer"
                title="Reset finish to default"
              >
                <RotateCcw className="w-2.5 h-2.5" />
                <span>Reset</span>
              </button>
            </div>

            <div className="grid grid-cols-3 gap-1.5">
              {MATERIAL_FINISH_PRESETS.slice(0, 6).map((preset) => {
                const isActive = currentFinish === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleSelectPreset(preset.id)}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left transition-all cursor-pointer ${
                      isActive
                        ? 'bg-amber-500/20 border-amber-500 text-amber-200 font-bold shadow-xs'
                        : 'bg-zinc-950/70 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                    }`}
                  >
                    <span className="text-sm shrink-0">{preset.icon}</span>
                    <span className="text-[10px] truncate leading-tight">{preset.name.replace(/^(24K|Chrome|Raised|Soft-Touch|3D)\s*/, '')}</span>
                  </button>
                );
              })}
            </div>
            
            {/* Secondary presets row */}
            <div className="grid grid-cols-3 gap-1.5 mt-1.5">
              {MATERIAL_FINISH_PRESETS.slice(6).map((preset) => {
                const isActive = currentFinish === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleSelectPreset(preset.id)}
                    className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg border text-left transition-all cursor-pointer ${
                      isActive
                        ? 'bg-amber-500/20 border-amber-500 text-amber-200 font-bold shadow-xs'
                        : 'bg-zinc-950/70 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700'
                    }`}
                  >
                    <span className="text-sm shrink-0">{preset.icon}</span>
                    <span className="text-[10px] truncate leading-tight">{preset.name.replace(/^(24K|Chrome|Raised|Soft-Touch|3D|Holographic)\s*/, '')}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Foil Tint Swatches (if metallic or foil) */}
          {['gold_foil', 'silver_foil', 'custom_metallic', 'holographic'].includes(currentFinish) && (
            <div className="space-y-1.5 bg-zinc-950/80 p-2.5 rounded-lg border border-zinc-800/80">
              <div className="flex items-center justify-between text-[10px]">
                <span className="font-semibold text-zinc-300">Metallic Foil Tint:</span>
                <span className="font-mono text-amber-400 font-bold">{currentColor}</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={currentColor}
                  onChange={(e) => applyMaterialUpdates({ color: e.target.value })}
                  className="w-7 h-7 rounded bg-transparent border border-zinc-700 cursor-pointer shrink-0"
                  title="Choose custom metallic color"
                />
                <div className="flex items-center gap-1 overflow-x-auto custom-scrollbar pb-0.5">
                  {QUICK_FOIL_SWATCHES.map((sw) => (
                    <button
                      key={sw.name}
                      type="button"
                      onClick={() => applyMaterialUpdates({ color: sw.color })}
                      className={`w-4 h-4 rounded-full border transition-transform shrink-0 ${
                        currentColor.toLowerCase() === sw.color.toLowerCase()
                          ? 'scale-125 border-white ring-2 ring-amber-500'
                          : 'border-zinc-700 hover:scale-110'
                      }`}
                      style={{ backgroundColor: sw.color }}
                      title={sw.name}
                    ></button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* CORE PBR PROPERTIES: Roughness & Metallic */}
          <div className="space-y-2.5 pt-2 border-t border-zinc-800">
            
            {/* Metallic / Metalness Slider */}
            <div className="space-y-1">
              <div className="flex justify-between text-[10.5px] font-mono">
                <span className="text-zinc-300 font-semibold flex items-center gap-1">
                  <span>Metallic / Metalness:</span>
                </span>
                <span className="text-amber-300 font-bold">{Math.round(currentMetalness * 100)}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.02"
                value={currentMetalness}
                onChange={(e) => applyMaterialUpdates({ 
                  metalness: parseFloat(e.target.value),
                  finish: currentFinish === 'default' && parseFloat(e.target.value) > 0.2 ? 'custom_metallic' : currentFinish
                })}
                className="w-full accent-amber-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
              />
              <div className="flex justify-between text-[8.5px] text-zinc-500 font-mono">
                <span>0% Non-Metallic (Ink)</span>
                <span>100% Mirror Specular</span>
              </div>
            </div>

            {/* Roughness / Gloss Slider */}
            <div className="space-y-1">
              <div className="flex justify-between text-[10.5px] font-mono">
                <span className="text-zinc-300 font-semibold flex items-center gap-1">
                  <span>Roughness / Surface Sheen:</span>
                </span>
                <span className="text-cyan-300 font-bold">
                  {Math.round(currentRoughness * 100)}% ({currentRoughness < 0.2 ? 'Mirror Gloss' : currentRoughness > 0.75 ? 'Deep Matte' : 'Satin Sheen'})
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.02"
                value={currentRoughness}
                onChange={(e) => applyMaterialUpdates({ roughness: parseFloat(e.target.value) })}
                className="w-full accent-cyan-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
              />
              <div className="flex justify-between text-[8.5px] text-zinc-500 font-mono">
                <span>0% High Gloss (Wet Lacquer)</span>
                <span>100% Velvet Matte (Diffuse)</span>
              </div>
            </div>

            {/* 3D Tactile Relief Depth / Displacement */}
            <div className="space-y-1.5 bg-purple-950/20 p-2.5 rounded-lg border border-purple-900/40">
              <div className="flex justify-between text-[10.5px] font-mono">
                <span className="text-purple-200 font-semibold flex items-center gap-1">
                  <span>3D Relief Depth (Emboss/Deboss):</span>
                </span>
                <span className={`font-bold ${currentRelief > 0 ? 'text-emerald-400' : currentRelief < 0 ? 'text-amber-400' : 'text-zinc-400'}`}>
                  {currentRelief > 0 ? `+${currentRelief.toFixed(2)} mm (Emboss)` : currentRelief < 0 ? `${currentRelief.toFixed(2)} mm (Deboss)` : '0.00 mm (Flat)'}
                </span>
              </div>
              <input
                type="range"
                min="-2.0"
                max="2.0"
                step="0.05"
                value={currentRelief}
                onChange={(e) => applyMaterialUpdates({ 
                  relief: parseFloat(e.target.value),
                  finish: parseFloat(e.target.value) > 0.2 && currentFinish === 'default' ? 'embossed' : 
                          parseFloat(e.target.value) < -0.2 && currentFinish === 'default' ? 'debossed' : currentFinish
                })}
                className="w-full accent-purple-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
              />
              <div className="flex justify-between text-[8.5px] text-zinc-500 font-mono">
                <span className="text-amber-400/80">-2.0mm Deboss</span>
                <span>0.0mm Flat</span>
                <span className="text-emerald-400/80">+2.0mm Emboss</span>
              </div>
            </div>
          </div>

          {/* DETAIL SETTINGS: BUMP MAPS & TACTILE TEXTURE */}
          <div className="pt-2 border-t border-zinc-800 space-y-2.5">
            <div 
              onClick={() => setShowBumpDetails(!showBumpDetails)}
              className="flex items-center justify-between cursor-pointer select-none text-zinc-300 hover:text-white"
            >
              <div className="flex items-center gap-1.5 font-bold text-[11px]">
                <Layers className="w-3.5 h-3.5 text-indigo-400" />
                <span>Tactile Bump Map & Texture Details</span>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[9.5px] font-mono text-indigo-400 font-semibold">
                  {BUMP_MAP_PRESETS.find(b => b.id === currentBumpMap)?.name || 'Custom'}
                </span>
                {showBumpDetails ? <ChevronUp className="w-3 h-3 text-zinc-500" /> : <ChevronDown className="w-3 h-3 text-zinc-500" />}
              </div>
            </div>

            {showBumpDetails && (
              <div className="space-y-3 bg-zinc-950/60 p-2.5 rounded-lg border border-zinc-800/80">
                
                {/* Bump Texture Selector Dropdown */}
                <div>
                  <label className="text-[10px] text-zinc-400 block mb-1 font-medium">
                    Surface Bump Texture Pattern:
                  </label>
                  <select
                    value={currentBumpMap}
                    onChange={(e) => applyMaterialUpdates({ bumpMapType: e.target.value as ElementMaterialBumpMap })}
                    className="w-full bg-zinc-900 border border-zinc-700 text-zinc-200 text-xs px-2.5 py-1.5 rounded-lg outline-none focus:border-indigo-500 font-medium"
                  >
                    {BUMP_MAP_PRESETS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.icon} {b.name} - {b.desc}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Bump Scale / Height Intensity Slider */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] font-mono">
                    <span className="text-zinc-400">Bump Height Intensity:</span>
                    <span className="text-indigo-400 font-bold">{(currentBumpScale * 100).toFixed(0)}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="2.0"
                    step="0.05"
                    value={currentBumpScale}
                    onChange={(e) => applyMaterialUpdates({ bumpScale: parseFloat(e.target.value) })}
                    className="w-full accent-indigo-500 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                  />
                  <div className="flex justify-between text-[8.5px] text-zinc-500 font-mono">
                    <span>Subtle Micro (0%)</span>
                    <span>Standard (100%)</span>
                    <span>Heavy Deep (200%)</span>
                  </div>
                </div>

                {/* Bump Repeat / Tiling Frequency */}
                <div className="space-y-1">
                  <div className="flex justify-between text-[10px] font-mono">
                    <span className="text-zinc-400">Texture Tiling Density:</span>
                    <span className="text-indigo-300 font-bold">{currentBumpRepeat}× Frequency</span>
                  </div>
                  <input
                    type="range"
                    min="1"
                    max="20"
                    step="1"
                    value={currentBumpRepeat}
                    onChange={(e) => applyMaterialUpdates({ bumpRepeat: parseInt(e.target.value) })}
                    className="w-full accent-indigo-400 cursor-pointer h-1.5 bg-zinc-800 rounded-lg"
                  />
                  <div className="flex justify-between text-[8.5px] text-zinc-500 font-mono">
                    <span>1× Coarse</span>
                    <span>10× Medium</span>
                    <span>20× Ultra-Fine Micro</span>
                  </div>
                </div>

                {/* Invert Bump & Bevel Softness Row */}
                <div className="grid grid-cols-2 gap-2 pt-1 border-t border-zinc-800/80">
                  {/* Invert Bump (Emboss vs Deboss) */}
                  <div className="flex items-center justify-between p-2 rounded-lg bg-zinc-900 border border-zinc-800">
                    <label className="text-[10px] text-zinc-300 font-medium">Invert Bump:</label>
                    <input
                      type="checkbox"
                      checked={currentInvertBump}
                      onChange={(e) => applyMaterialUpdates({ invertBump: e.target.checked })}
                      className="w-4 h-4 rounded accent-indigo-600 cursor-pointer"
                    />
                  </div>

                  {/* Bevel Edge Softness */}
                  <div className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 space-y-1">
                    <div className="flex justify-between text-[9.5px] font-mono">
                      <span className="text-zinc-400">Bevel Edge:</span>
                      <span className="text-zinc-300 font-bold">{Math.round(currentBevelSoftness * 100)}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={currentBevelSoftness}
                      onChange={(e) => applyMaterialUpdates({ bevelSoftness: parseFloat(e.target.value) })}
                      className="w-full accent-zinc-400 cursor-pointer h-1 bg-zinc-800 rounded-lg"
                    />
                  </div>
                </div>

                {/* Live Micro Texture Preview Swatch */}
                <div className="p-2 rounded-lg bg-gradient-to-r from-zinc-900 via-zinc-800 to-zinc-900 border border-zinc-700/60 flex items-center justify-between text-[10px]">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-6 h-6 rounded border border-white/20 shadow-inner shrink-0"
                      style={{
                        backgroundColor: currentColor,
                        backgroundImage: currentBumpMap === 'brushed_metal'
                          ? 'repeating-linear-gradient(90deg, rgba(0,0,0,0.15) 0px, rgba(255,255,255,0.2) 2px, rgba(0,0,0,0.15) 4px)'
                          : currentBumpMap === 'micro_diamond'
                          ? 'radial-gradient(circle, rgba(0,0,0,0.3) 1px, transparent 1px)'
                          : currentBumpMap === 'hammered_foil'
                          ? 'radial-gradient(circle at 30% 30%, rgba(255,255,255,0.4), rgba(0,0,0,0.3))'
                          : 'none',
                        backgroundSize: '8px 8px',
                        boxShadow: currentRelief > 0 ? 'inset 0 1px 2px rgba(255,255,255,0.6), 0 2px 4px rgba(0,0,0,0.4)' : 'inset 0 2px 4px rgba(0,0,0,0.6)'
                      }}
                    />
                    <div className="flex flex-col">
                      <span className="font-semibold text-zinc-200">{activePreset.name}</span>
                      <span className="text-[9px] text-zinc-400 font-mono">
                        {Math.round(currentMetalness * 100)}% Met • {Math.round(currentRoughness * 100)}% Rgh • {currentRelief > 0 ? `+${currentRelief.toFixed(1)}mm` : `${currentRelief.toFixed(1)}mm`}
                      </span>
                    </div>
                  </div>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono">
                    3D PBR
                  </span>
                </div>

              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
};
